/** Describing a figure, numbering it and annotating it, shown before the server agrees. */

import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { FigureListResponse, PointModel } from "@patent-studio/api-types";
import { foldWalkthrough } from "./walkthrough-fold";
import { useLiftedConfirmations } from "./use-lifted-confirmations";
import { type DefinitionsWrite, writeQueued } from "./definitions-write";
import type {
  ConnectorDefaults,
  ConnectorShape,
  Definitions,
  ElementRow,
  FigureRow,
  NumeralStyle,
  NumberingConvention,
} from "@/lib/element-registry";
import { markEntryPoints } from "./figure-mark-entry-points";
import { nestingEntryPoints } from "./figure-nesting-entry-points";
import {
  confirmTitledGroups,
  nest,
  restoreGroup,
  takeOut,
  type UndoneDrop,
} from "@/lib/figure-registry";
import {
  adopt as adoptPin,
  aimTip,
  bend,
  confirmElement,
  confirmTitledOn,
  movePin,
  place,
  removeInstance,
  retitle,
  setArrowhead,
  setConnectorDefaults,
  setConnectorPresence,
  setConnectorShape,
  setMarkNumeralStyle,
  setNumeralStyle,
  setNumberingConvention,
  setOverride,
  clearOverride,
  definitionsOf,
  elementsOn,
  withFigure,
} from "@/lib/element-registry";
import { figuresKey } from "./query-keys";
import { useFigures } from "./use-figures";
import { drawingOf, useWholeFigureActs } from "./use-whole-figure-acts";

/**
 * One change the attorney asked for, carrying its outcome rather than its intent.
 *
 * Every field is settled before the change is issued: which element, which id, which point,
 * which title. Nothing here says "the next free number" or "the pin under the cursor",
 * because a change waits in the queue and is folded over whatever set is freshest when its
 * turn comes, and an intent resolved twice can resolve to two different things.
 */
export type DefinitionChange =
  | { kind: "walkthrough"; fileId: string; walkthrough: string }
  | { kind: "description"; fileId: string; description: string }
  | { kind: "walkthrough-accepted"; fileId: string; accepted: false }
  | {
      kind: "walkthrough-accepted";
      fileId: string;
      accepted: true;
      walkthrough: string;
    }
  | {
      kind: "adopt-transcript";
      fileId: string;
      clipId: string;
      transcript: string;
    }
  | { kind: "attach-walkthrough-clip"; fileId: string; clipId: string }
  | { kind: "number"; fileId: string }
  | { kind: "unnumber"; fileId: string }
  | { kind: "prior-art"; fileId: string; priorArt: boolean }
  | {
      kind: "place";
      fileId: string;
      id: string;
      elementId: string;
      pin: PointModel;
      tip: PointModel;
      // Carried rather than read when the fold runs. A change waits its turn and is folded
      // again over whatever set is freshest, and conventions read then would mint a
      // connector the attorney never saw for a mark they had already placed.
      defaults: ConnectorDefaults;
    }
  | { kind: "set-override"; elementId: string; value: number }
  | { kind: "clear-override"; elementId: string }
  | { kind: "adopt"; fileId: string; id: string; toElementId: string }
  | { kind: "remove-pin"; fileId: string; id: string }
  | { kind: "retitle"; elementId: string; title: string }
  | { kind: "confirm"; elementId: string }
  | { kind: "confirm-titled"; fileId: string }
  | { kind: "move-pin"; fileId: string; id: string; pin: PointModel }
  | { kind: "aim-tip"; fileId: string; id: string; tip: PointModel }
  | {
      kind: "bend";
      fileId: string;
      id: string;
      index: number;
      control: PointModel;
    }
  | {
      kind: "connector-shape";
      fileId: string;
      id: string;
      shape: ConnectorShape;
      // Stated by the change rather than read off the conventions when the fold runs. The
      // attorney picked a bow or an S from the toolbar, and a replay that read the set would
      // hand back the other one whenever the house style disagreed with what they clicked.
      anchors: ConnectorDefaults["anchors"];
      defaults: ConnectorDefaults;
    }
  | { kind: "connector-arrowhead"; fileId: string; id: string; on: boolean }
  | {
      kind: "connector-presence";
      fileId: string;
      id: string;
      present: boolean;
      defaults: ConnectorDefaults;
    }
  | { kind: "connector-defaults"; defaults: ConnectorDefaults }
  | { kind: "numeral-style"; style: NumeralStyle }
  | { kind: "numbering-convention"; numbering_convention: NumberingConvention }
  | {
      kind: "mark-numeral-style";
      fileId: string;
      id: string;
      style: NumeralStyle;
    }
  | { kind: "nest"; dragged: string; target: string; groupId: string }
  | { kind: "take-out"; elementId: string }
  | {
      kind: "restore-group";
      group: ElementRow;
      memberIds: string[];
      undoing?: UndoneDrop;
    };

/**
 * Whether this drawing's numbering may be taken back off.
 *
 * Only while nothing is pinned to it. Turning numbering on is easy to do by
 * accident and leaves the figure reading as unfinished for ever, so it has to
 * be recoverable; but once a pin exists, taking numbering off would discard the
 * attorney's work, and no undo is worth that. The two are the same rule read at
 * two moments, which is why it is one predicate rather than a condition at each
 * call site.
 */
export function canRemoveNumbering(row: FigureRow | undefined): boolean {
  return (
    row !== undefined && row.numbered && (row.instances ?? []).length === 0
  );
}

/**
 * A drawing's definition as it stands, or the one it would have.
 *
 * The stored set holds a row per drawing that has one, not per figure on the
 * task, so the first thing typed about a figure appends rather than edits.
 */
function rowFor(definitions: Definitions, fileId: string): FigureRow {
  const existing = definitions.figures.find((f) => f.file_id === fileId);
  return (
    existing ?? {
      file_id: fileId,
      numbered: false,
      description: "",
      walkthrough: "",
      walkthrough_file_ids: [],
      walkthrough_accepted: false,
      instances: [],
      prior_art: false,
    }
  );
}

/** The set with one figure's own fields changed, its elements untouched. */
function figureChanged(
  definitions: Definitions,
  fileId: string,
  change: Partial<FigureRow>,
): Definitions {
  return withFigure(definitions, fileId, {
    ...rowFor(definitions, fileId),
    ...change,
  });
}

/** The set with one field the attorney sets on a figure by itself changed. */
function fieldChanged(
  definitions: Definitions,
  change: Extract<DefinitionChange, { kind: "description" | "prior-art" }>,
): Definitions {
  return figureChanged(
    definitions,
    change.fileId,
    change.kind === "description"
      ? { description: change.description }
      : { prior_art: change.priorArt },
  );
}

/**
 * The definitions the change asks for, against the set given.
 *
 * This is the one fold step: the view applies it over the whole queue, and the write applies
 * it over the freshest server set when the change's turn comes. Every other row rides
 * through untouched, which is what keeps a walk-through save from costing the attorney their
 * annotation work: the write replaces the whole set, so a row this rebuild dropped would be
 * a row the server forgets. The element rules live in `element-registry` rather than here,
 * because they are decisions about the whole set and the screen has to reach the same
 * answers this seam does.
 */
function definitionsFor(
  change: DefinitionChange,
  definitions: Definitions,
): Definitions {
  switch (change.kind) {
    case "walkthrough":
    case "walkthrough-accepted":
      return foldWalkthrough(change, definitions, rowFor, figureChanged);
    case "adopt-transcript":
      return foldWalkthrough(change, definitions, rowFor, figureChanged);
    case "attach-walkthrough-clip": {
      const held =
        rowFor(definitions, change.fileId).walkthrough_file_ids ?? [];
      if (held.includes(change.clipId)) return definitions;
      return figureChanged(definitions, change.fileId, {
        walkthrough_file_ids: [...held, change.clipId],
      });
    }
    case "description":
    case "prior-art":
      return fieldChanged(definitions, change);
    case "number":
      return figureChanged(definitions, change.fileId, { numbered: true });
    case "unnumber":
      return figureChanged(definitions, change.fileId, { numbered: false });
    case "place":
      return place(definitions, change);
    case "set-override":
      return setOverride(definitions, change.elementId, change.value);
    case "clear-override":
      return clearOverride(definitions, change.elementId);
    case "adopt":
      return adoptPin(definitions, change);
    case "remove-pin":
      return removeInstance(definitions, change);
    case "retitle":
      return retitle(definitions, change.elementId, change.title);
    case "confirm":
      return confirmElement(definitions, change.elementId);
    case "confirm-titled":
      return confirmTitledHere(definitions, change.fileId);
    case "move-pin":
      return movePin(definitions, change);
    case "aim-tip":
      return aimTip(definitions, change);
    case "bend":
      return bend(definitions, change);
    case "connector-shape":
      return setConnectorShape(definitions, change);
    case "connector-arrowhead":
      return setArrowhead(definitions, change);
    case "connector-presence":
      return setConnectorPresence(definitions, change);
    case "connector-defaults":
      return setConnectorDefaults(definitions, change.defaults);
    case "numeral-style":
      return setNumeralStyle(definitions, change.style);
    case "numbering-convention":
      return setNumberingConvention(definitions, change.numbering_convention);
    case "mark-numeral-style":
      return setMarkNumeralStyle(definitions, {
        fileId: change.fileId,
        id: change.id,
        numeral_style: change.style,
      });
    case "nest":
      return withRegistry(definitions, nest(definitions.registry, change));
    case "take-out":
      return withRegistry(
        definitions,
        takeOut(definitions.registry, change.elementId),
      );
    case "restore-group":
      return withRegistry(
        definitions,
        restoreGroup(definitions.registry, change),
      );
  }
}

/** Confirm all on one figure: the titled elements pinned here, then the group rows above them. */
function confirmTitledHere(
  definitions: Definitions,
  fileId: string,
): Definitions {
  const titled = confirmTitledOn(definitions, fileId);
  return withRegistry(
    titled,
    confirmTitledGroups(titled.registry, elementsOn(titled, fileId)),
  );
}

/**
 * The set with a nesting edit's registry, or the same set when the edit changed nothing.
 *
 * A drop refused as full when it was asked for is not issued, but a change folded again over
 * a fresher set can meet a group another write filled, and then it lands nowhere.
 */
function withRegistry(
  definitions: Definitions,
  registry: readonly ElementRow[] | "full",
): Definitions {
  if (registry === "full" || registry === definitions.registry)
    return definitions;
  return { ...definitions, registry: [...registry] };
}

/**
 * The change an accept or amend resolves to.
 *
 * Acceptance is of particular words, so the accept carries the words on
 * screen — the card's own draft, which no view has seen yet — and writes
 * them beside the bit in one change. An amend carries nothing: taking the
 * mark off is safe over any words.
 */
function acceptance(
  fileId: string,
  accepted: boolean,
  walkthrough: string,
): DefinitionChange {
  if (!accepted) return { kind: "walkthrough-accepted", fileId, accepted };
  return { kind: "walkthrough-accepted", fileId, accepted, walkthrough };
}

/**
 * What the screen may ask for, each call turned into the one change that says it.
 *
 * Outside the hook because none of it needs React: an entry point resolves its arguments into
 * a settled change and hands it over, and everything about queueing, showing it early and
 * reconciling belongs to the seam below rather than to any one of these.
 */
function entryPoints(
  issue: (change: DefinitionChange) => void,
  save: (change: DefinitionChange) => Promise<void>,
  showing: () => Definitions,
) {
  return {
    describeWalkthrough: (fileId: string, walkthrough: string) =>
      save({ kind: "walkthrough", fileId, walkthrough }),

    /** The brief description the draft prints for this figure, as the attorney wrote it. */
    describeFigure: (fileId: string, description: string) =>
      save({ kind: "description", fileId, description }),

    adoptTranscript: (fileId: string, clipId: string, transcript: string) =>
      issue({ kind: "adopt-transcript", fileId, clipId, transcript }),

    acceptWalkthrough: (fileId: string, accepted: boolean, walkthrough = "") =>
      issue(acceptance(fileId, accepted, walkthrough)),

    attachWalkthroughClip: (fileId: string, clipId: string) =>
      issue({ kind: "attach-walkthrough-clip", fileId, clipId }),

    addNumbering: (fileId: string) => issue({ kind: "number", fileId }),

    /** Whether the drawing shows the prior art, which puts the legend "Prior Art" on its sheet. */
    markPriorArt: (fileId: string, priorArt: boolean) =>
      issue({ kind: "prior-art", fileId, priorArt }),

    // Guarded here as well as at the control, because the rule is about what may be written
    // and not about what may be drawn. A screen that offered the control by mistake still
    // cannot discard a pin through it.
    removeNumbering: (fileId: string) => {
      const row = showing().figures.find((figure) => figure.file_id === fileId);
      if (!canRemoveNumbering(row)) return;
      issue({ kind: "unnumber", fileId });
    },

    /**
     * Pin an element to a drawing, minting a new one unless an id is named.
     *
     * The pin's own id and the element's id are settled here rather than in the reducer so
     * the change carries them, which is what lets the fold apply it as often as it needs to
     * without minting a second of either. Digits are projected later from registry order;
     * they are not this identity.
     */
    place: (
      fileId: string,
      placement: { pin: PointModel; tip: PointModel; elementId?: string },
    ) =>
      issue({
        kind: "place",
        fileId,
        id: crypto.randomUUID(),
        elementId: placement.elementId ?? crypto.randomUUID(),
        pin: placement.pin,
        tip: placement.tip,
        defaults: showing().settings.connector_defaults,
      }),

    retitle: (elementId: string, title: string) =>
      save({ kind: "retitle", elementId, title }),

    confirm: (elementId: string) => issue({ kind: "confirm", elementId }),

    confirmTitled: (fileId: string) =>
      issue({ kind: "confirm-titled", fileId }),

    setOverride: (elementId: string, value: number) =>
      issue({ kind: "set-override", elementId, value }),

    clearOverride: (elementId: string) =>
      issue({ kind: "clear-override", elementId }),

    adopt: (fileId: string, move: { id: string; toElementId: string }) =>
      issue({ kind: "adopt", fileId, ...move }),

    /**
     * Hand every mark of one element over to an element the set already holds.
     *
     * The registry is set-wide, so this covers the marks on every drawing rather than the
     * open one: leaving the others behind would keep the abandoned element alive under its
     * own name, which is the duplication the reuse exists to end. What arrives instead is
     * one adopt per mark, with the marks settled here at pick time so each change carries
     * the id it moves rather than an intent that would resolve twice.
     */
    reuseElement: (fromElementId: string, toElementId: string) => {
      for (const figure of showing().figures) {
        for (const instance of figure.instances ?? []) {
          if (instance.element_id !== fromElementId) continue;
          issue({
            kind: "adopt",
            fileId: figure.file_id,
            id: instance.id,
            toElementId,
          });
        }
      }
    },
    ...markEntryPoints(issue, showing),
    ...nestingEntryPoints(issue, showing),
  };
}

/** A save for the changes nobody waits on: a failure is told by the toast alone. */
function withoutWaiting(save: (change: DefinitionChange) => Promise<void>) {
  return (change: DefinitionChange) => {
    save(change).catch(() => undefined);
  };
}

/**
 * The acknowledged set and the view the screen draws, which is the pending list folded over it.
 *
 * The acknowledged set on its own is for a field that owns a draft. Such a field compares
 * its draft with what is stored to know when its save has landed; read off the folded
 * view, the two agree the moment the change is queued, and a refusal that drops the
 * change would then look like a newer stored value and overwrite the attorney's words.
 */
function useDefinitionsView(
  stored: FigureListResponse["definitions"],
  pending: readonly DefinitionChange[],
) {
  const served = useMemo(() => definitionsOf(stored), [stored]);
  const definitions = useMemo(
    () => pending.reduce((set, change) => definitionsFor(change, set), served),
    [pending, served],
  );
  return { served, definitions };
}

/**
 * The attorney's figure definitions: the view to draw, and the entry points that change it.
 *
 * The cache only ever holds what the store has acknowledged. Changes the attorney has made
 * that the store has not confirmed wait in a pending list, and the view is the fold of that
 * list over the acknowledged set, so each change is applied exactly once per look rather
 * than once to the screen and again at write time. That is what lets a failure roll back by
 * dropping one entry, and a conflict adopt the store's set without taking later, still
 * queued changes off the screen.
 *
 * Writes drain the list one at a time through one queue, because each write replaces the
 * whole set: two in flight together are settled by whichever the server applies last, and
 * that is not necessarily the state the attorney left behind.
 *
 * One instance per screen. The pending list is this instance's state, so a second instance
 * would show a view missing the first one's unacknowledged changes.
 */
export function useFigureDefinitions(taskId: string | undefined) {
  const qc = useQueryClient();
  // Held across renders, because the key is what the entry points are memoised on and the
  // factory builds a fresh array each call.
  const key = useMemo(() => figuresKey(taskId ?? ""), [taskId]);
  const { data } = useFigures(taskId);

  const [pending, setPending] = useState<readonly DefinitionChange[]>([]);
  // The same list, readable outside the render: the queue drains between renders, and the
  // entry points that read before they issue need the list as it stands now. Removal is by
  // identity, which is exact because every issued change is a fresh object.
  const pendingRef = useRef<readonly DefinitionChange[]>([]);
  const inFlightRef = useRef<Promise<unknown>>(Promise.resolve());
  // Drawings the store has been told to remove. The screen can still be showing one, because
  // it leaves only when the refreshed listing no longer names it, and a change made in that
  // gap has nowhere to land. Refused here rather than by the store, which would answer a
  // deliberate delete with a save error.
  const goneRef = useRef(new Set<string>());
  const [lifted, noteLift] = useLiftedConfirmations(definitionsFor);
  const queue = useMemo<DefinitionsWrite>(
    () => ({ qc, key, fold: definitionsFor, pending: pendingRef, setPending }),
    [qc, key],
  );

  // The set as the screen reads it right now, for the entry points that must read before
  // they issue.
  const showing = useCallback(
    () =>
      pendingRef.current.reduce(
        (set, change) => definitionsFor(change, set),
        definitionsOf(qc.getQueryData<FigureListResponse>(key)?.definitions),
      ),
    [key, qc],
  );

  // A typed field waits on its own write, so it can keep the words and offer a retry when
  // the write fails; every other change is issued and told of only through the toast.
  const save = useCallback(
    (change: DefinitionChange): Promise<void> => {
      if (taskId === undefined) return Promise.resolve();
      const drawing = drawingOf(change);
      if (drawing !== undefined && goneRef.current.has(drawing))
        return Promise.resolve();
      noteLift(change, showing());
      pendingRef.current = [...pendingRef.current, change];
      setPending(pendingRef.current);

      const run = () => writeQueued(queue, taskId, change, save);
      // The next write must follow this one whether this one landed or failed, while each
      // failure still reaches its own handler above.
      const settled = inFlightRef.current.then(run, run);
      inFlightRef.current = settled.catch(() => undefined);
      return settled;
    },
    [queue, taskId, noteLift, showing],
  );
  const issue = useMemo(() => withoutWaiting(save), [save]);

  const { deleteFigure, restoreFigure, purgeFigure } = useWholeFigureActs({
    qc,
    key,
    taskId,
    inFlight: inFlightRef,
    pending: pendingRef,
    setPending,
    gone: goneRef,
    issue,
  });

  const { served, definitions } = useDefinitionsView(
    data?.definitions,
    pending,
  );

  const entry = useMemo(
    () => entryPoints(issue, save, showing),
    [issue, save, showing],
  );
  return useMemo(
    () => ({
      definitions,
      served,
      lifted,
      deleteFigure,
      restoreFigure,
      purgeFigure,
      ...entry,
    }),
    [
      definitions,
      served,
      lifted,
      deleteFigure,
      restoreFigure,
      purgeFigure,
      entry,
    ],
  );
}
